import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { Loader, LoaderModeType } from 'components/Loader';
import { TotemSelect } from 'components/TotemSelect';
import { TotemInput } from 'components/TotemInput';
import { TotemSwitch } from 'components/TotemSwitch';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { MicrostoreColumnTemplateFormValues, MicrostoreLocationTemplateFormValues } from './FormHelper/DataMapper';
import {
    ProductArrangementReassortmentType,
    ProductArrangementWithProductsFragmentFragment,
    useGetProductArrangementLazyQuery,
    useGetProductArrangementsQuery,
    ProductState,
    ProductArrangementProductFragmentFragment,
    TotadminPermission,
} from 'data/__generated__';
import { PRODUCT_ARRANGEMENT_REASSORTMENT_TYPE_LABELS } from 'pages/ProductArrangements/ProductArrangementDetails/constants/types';
import { ProductSelect } from 'components/Select/ProductSelect';
import { PermissionGate } from 'components/PermissionGate';

const DEFAULT_PRODUCTS_FILTERS = {
    states: [ProductState.Regular],
    isMicrostore: true,
};

type LocationConfigSidebarProps = {
    isOpen: boolean;
    microstoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
    selectedShelfTemplateIndex: number | null;
    selectedLocationTemplatePosition: { column: number; row: number } | null;
    setMicrostoreColumnTemplateFormValuesAndIsFormDirty: ({
        newMicrostoreColumnTemplateFormValues,
        newIsDirty,
    }: {
        newMicrostoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
        newIsDirty?: boolean | undefined;
    }) => void;
    onClose: () => void;
};

export const LocationConfigSidebar = ({
    isOpen,
    microstoreColumnTemplateFormValues,
    selectedShelfTemplateIndex,
    selectedLocationTemplatePosition,
    setMicrostoreColumnTemplateFormValuesAndIsFormDirty,
    onClose,
}: LocationConfigSidebarProps) => {
    const {
        loading: productArrangementsLoading,
        data: productArrangementsData,
        error: productArrangementsError,
    } = useGetProductArrangementsQuery();

    const [getProductArrangementAndAddToLocation, { loading: productArrangementLoading }] =
        useGetProductArrangementLazyQuery({
            fetchPolicy: 'cache-and-network',
            onCompleted: ({ productArrangementQuery }) => {
                if (productArrangementQuery) {
                    addProductArrangementToLocation(productArrangementQuery);
                }
            },
            onError: (error) => {
                toast.error(`Une erreur inattendue s'est produite : ${error.message}`);
            },
        });

    if (!isOpen) return null;

    if (productArrangementsLoading && !productArrangementsData) {
        return (
            <SidebarContainer $isOpen={isOpen}>
                <Loader mode={LoaderModeType.Spin} />
            </SidebarContainer>
        );
    }

    if (productArrangementsError || !productArrangementsData) {
        return (
            <SidebarContainer $isOpen={isOpen}>
                <ErrorMessage>Erreur lors du chargement des gammes de produits</ErrorMessage>
            </SidebarContainer>
        );
    }

    const { productArrangementsQuery: productArrangements } = productArrangementsData;

    const selectedShelfTemplate =
        selectedShelfTemplateIndex !== null
            ? microstoreColumnTemplateFormValues.shelfTemplates[selectedShelfTemplateIndex]
            : null;

    const selectedLocationTemplate =
        selectedShelfTemplate !== null &&
        selectedLocationTemplatePosition !== null &&
        selectedShelfTemplate.locationTemplateTable &&
        selectedLocationTemplatePosition.row < selectedShelfTemplate.locationTemplateTable.length &&
        selectedLocationTemplatePosition.column <
            selectedShelfTemplate.locationTemplateTable[selectedLocationTemplatePosition.row].length
            ? selectedShelfTemplate.locationTemplateTable[selectedLocationTemplatePosition.row][
                  selectedLocationTemplatePosition.column
              ]
            : null;

    if (!selectedLocationTemplate) {
        return (
            <SidebarContainer $isOpen={isOpen}>
                <SidebarHeader>
                    <HeaderTitle>Aucun emplacement sélectionné</HeaderTitle>
                    <CloseButton onClick={onClose}>
                        <FaTimes />
                    </CloseButton>
                </SidebarHeader>
                <EmptyState>Sélectionnez un emplacement pour le configurer</EmptyState>
            </SidebarContainer>
        );
    }

    function updateLocation(newLocationValues: Partial<MicrostoreLocationTemplateFormValues>) {
        if (
            selectedShelfTemplateIndex === null ||
            !selectedShelfTemplate ||
            selectedLocationTemplatePosition === null ||
            !selectedLocationTemplate
        ) {
            return;
        }

        const locationTemplateTable = selectedShelfTemplate.locationTemplateTable;
        const locationRow = locationTemplateTable[selectedLocationTemplatePosition.row];
        const location = locationRow[selectedLocationTemplatePosition.column];

        const newLocationRow = [...locationRow];
        newLocationRow[selectedLocationTemplatePosition.column] = { ...location, ...newLocationValues };

        const newLocationTemplateTable = [...locationTemplateTable];
        newLocationTemplateTable[selectedLocationTemplatePosition.row] = [...newLocationRow];

        const newShelfTemplates = [...microstoreColumnTemplateFormValues.shelfTemplates];
        newShelfTemplates[selectedShelfTemplateIndex] = {
            ...selectedShelfTemplate,
            locationTemplateTable: newLocationTemplateTable,
        };

        setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
            newMicrostoreColumnTemplateFormValues: {
                ...microstoreColumnTemplateFormValues,
                shelfTemplates: newShelfTemplates,
            },
        });
    }

    function addProductArrangementToLocation(
        productArrangement: ProductArrangementWithProductsFragmentFragment,
        alternativeProductArrangementIds?: string[] | null,
    ) {
        updateLocation({
            productArrangement,
            alternativeProductArrangementIds,
            product: null,
        });
    }

    function onProductSelect(product: ProductArrangementProductFragmentFragment | null) {
        if (!product) {
            return;
        }

        if (product.isWithoutStock) {
            updateLocation({
                productArrangement: null,
                product,
                reassortmentType: ProductArrangementReassortmentType.HotDrink,
                stockMax: null,
                trayMaxCapacity: 0,
            });
            return;
        }

        updateLocation({
            productArrangement: null,
            product,
        });
    }

    function clearLocation() {
        updateLocation({
            productArrangement: null,
            product: null,
            reassortmentType: null,
            stockMax: null,
            shouldOrderProductIfNotInSite: false,
            shouldSmoothProductTransition: true,
            allowProductRotation: true,
            trayMaxCapacity: 0,
        });
        toast.success('Emplacement vidé');
    }

    const productArrangementOptions =
        productArrangements
            .map(({ _id: productArrangementId, name }) => ({
                value: productArrangementId,
                label: name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const productArrangementOptionsWithManualReassortment =
        productArrangements
            .filter(({ reassortmentType }) => reassortmentType === ProductArrangementReassortmentType.Manual)
            .map(({ _id: productArrangementId, name }) => ({
                value: productArrangementId,
                label: name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const displayAlternativeProductArrangementIds =
        selectedLocationTemplate?.productArrangement?.reassortmentType === ProductArrangementReassortmentType.Manual;

    const reassortmentTypeOptions = Object.keys(ProductArrangementReassortmentType)
        .map((type) => ({
            value: type as ProductArrangementReassortmentType,
            label: PRODUCT_ARRANGEMENT_REASSORTMENT_TYPE_LABELS[type as ProductArrangementReassortmentType],
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const isStockReassortmentType =
        selectedLocationTemplate.reassortmentType === ProductArrangementReassortmentType.Manual ||
        selectedLocationTemplate.reassortmentType === ProductArrangementReassortmentType.Target;

    const hasStockAndTransitionOptions = isStockReassortmentType || !selectedLocationTemplate.reassortmentType;

    const isProductWithoutStock = !!selectedLocationTemplate.product?.isWithoutStock;

    const locationName = `E${(selectedShelfTemplateIndex ?? 0) + 1}-R${(selectedLocationTemplatePosition?.row ?? 0) + 1}-C${(selectedLocationTemplatePosition?.column ?? 0) + 1}`;

    return (
        <SidebarContainer $isOpen={isOpen}>
            {/* Header */}
            <SidebarHeader>
                <HeaderTitle>📦 Emplacement {locationName}</HeaderTitle>
                <CloseButton onClick={onClose}>
                    <FaTimes />
                </CloseButton>
            </SidebarHeader>

            {/* Scrollable Content */}
            <SidebarContent>
                {/* Section: Arrangement produit (Manual/Target uniquement) */}
                {hasStockAndTransitionOptions && (
                    <Section>
                        <SectionTitle>🏷️ ARRANGEMENT PRODUIT</SectionTitle>
                        <PermissionGate requiredPermissions={[TotadminPermission.ProductArrangementsRead]}>
                            <TotemSelect<string>
                                label="Choisir une gamme de produit"
                                placeholder="Sélectionner une gamme de produit"
                                value={selectedLocationTemplate?.productArrangement?._id ?? null}
                                options={productArrangementOptions}
                                onChange={(value) => {
                                    if (value) {
                                        getProductArrangementAndAddToLocation({
                                            variables: {
                                                productArrangementId: value,
                                            },
                                        });
                                    }
                                }}
                            />
                        </PermissionGate>

                        {displayAlternativeProductArrangementIds && (
                            <div style={{ marginTop: '12px' }}>
                                <TotemSelect
                                    isMulti
                                    label="Gammes alternatives"
                                    placeholder="Sélectionner des gammes alternatives"
                                    value={selectedLocationTemplate?.alternativeProductArrangementIds ?? []}
                                    options={productArrangementOptionsWithManualReassortment}
                                    onChange={(values) => {
                                        updateLocation({ alternativeProductArrangementIds: values });
                                    }}
                                />
                            </div>
                        )}
                    </Section>
                )}

                {/* Section: Produit individuel */}
                <Section>
                    <SectionTitle>🥤 PRODUIT INDIVIDUEL</SectionTitle>
                    <ProductSelect
                        value={selectedLocationTemplate.product?._id ?? null}
                        selectedProduct={selectedLocationTemplate.product}
                        onChange={onProductSelect}
                        filters={DEFAULT_PRODUCTS_FILTERS}
                    />
                </Section>

                {/* Section: Type de réassort & Stock (si produit sans arrangement) */}
                <Section>
                    <SectionTitle>
                        🔄 {selectedLocationTemplate.product ? 'Type de réassort' : 'Type de réassort (optionnel)'}
                    </SectionTitle>
                    <TotemSelect
                        label="Type de réassort"
                        placeholder="Sélectionner un type de réassort"
                        value={selectedLocationTemplate.reassortmentType ?? null}
                        options={reassortmentTypeOptions}
                        disabled={isProductWithoutStock}
                        onChange={(value) => {
                            const isStockType =
                                value === ProductArrangementReassortmentType.Manual ||
                                value === ProductArrangementReassortmentType.Target;
                            updateLocation({
                                reassortmentType: value || null,
                                trayMaxCapacity:
                                    value === ProductArrangementReassortmentType.Tray
                                        ? selectedLocationTemplate.trayMaxCapacity
                                        : 0,
                                ...(isStockType
                                    ? {}
                                    : {
                                          stockMax: null,
                                      }),
                            });
                        }}
                        isClearable={selectedLocationTemplate.product ? false : true}
                    />
                    {isProductWithoutStock && (
                        <HelperText>Verrouillé car le produit n'a pas de gestion de stock.</HelperText>
                    )}
                    <div style={{ marginTop: '12px' }}>
                        {selectedLocationTemplate.reassortmentType === ProductArrangementReassortmentType.Tray && (
                            <TotemInput
                                label="Capacité max"
                                placeholder="0"
                                type="number"
                                step="1"
                                min="1"
                                $centerText
                                onChange={(value) =>
                                    updateLocation({
                                        trayMaxCapacity: value ? parseInt(value) : 0,
                                    })
                                }
                                value={selectedLocationTemplate.trayMaxCapacity?.toString() ?? '0'}
                                required
                            />
                        )}
                    </div>
                </Section>

                {hasStockAndTransitionOptions && (
                    <>
                        <Section>
                            <SectionTitle>
                                {selectedLocationTemplate.product ? '📦 Stock cible' : '📦 Stock maximum (optionnel)'}
                            </SectionTitle>
                            <StockInputsRow>
                                <TotemInput
                                    label="Stock max"
                                    placeholder="-"
                                    type="number"
                                    $centerText
                                    step="1"
                                    min="0"
                                    onChange={(newStockMax) =>
                                        updateLocation({
                                            stockMax: newStockMax ? parseInt(newStockMax) : null,
                                        })
                                    }
                                    value={selectedLocationTemplate.stockMax?.toString() ?? ''}
                                    required={selectedLocationTemplate.product ? true : false}
                                />
                            </StockInputsRow>
                        </Section>

                        {/* Section: Options de transition */}
                        <Section>
                            <SectionTitle>⚙️ OPTIONS DE TRANSITION</SectionTitle>
                            <SwitchWrapper>
                                <TotemSwitch
                                    label="⏳ Attendre que le produit actuel soit épuisé"
                                    checked={selectedLocationTemplate.shouldSmoothProductTransition}
                                    onChange={() => {
                                        updateLocation({
                                            shouldOrderProductIfNotInSite:
                                                !selectedLocationTemplate.shouldSmoothProductTransition
                                                    ? selectedLocationTemplate.shouldOrderProductIfNotInSite
                                                    : true,
                                            shouldSmoothProductTransition:
                                                !selectedLocationTemplate.shouldSmoothProductTransition,
                                        });
                                    }}
                                />
                            </SwitchWrapper>
                            <SwitchWrapper>
                                <TotemSwitch
                                    label="⚡ Commander en avance (même si ancien non écoulé)"
                                    checked={selectedLocationTemplate.shouldOrderProductIfNotInSite}
                                    disabled={!selectedLocationTemplate.shouldSmoothProductTransition}
                                    onChange={() => {
                                        updateLocation({
                                            shouldOrderProductIfNotInSite:
                                                !selectedLocationTemplate.shouldOrderProductIfNotInSite,
                                        });
                                    }}
                                />
                            </SwitchWrapper>
                            <SwitchWrapper>
                                <TotemSwitch
                                    label="🔄 Autoriser la rotation en cours de semaine"
                                    checked={selectedLocationTemplate.allowProductRotation}
                                    onChange={() => {
                                        updateLocation({
                                            allowProductRotation: !selectedLocationTemplate.allowProductRotation,
                                        });
                                    }}
                                />
                            </SwitchWrapper>
                        </Section>
                    </>
                )}

                {/* Section: Actions rapides */}
                <Section>
                    <SectionTitle>🚀 ACTIONS RAPIDES</SectionTitle>
                    <ActionsGrid>
                        <TotemPrimaryButton
                            type="button"
                            $isSecondaryStyle
                            onClick={clearLocation}
                            style={{ width: '100%' }}
                        >
                            🗑️ Vider l'emplacement
                        </TotemPrimaryButton>
                    </ActionsGrid>
                </Section>
            </SidebarContent>
        </SidebarContainer>
    );
};

const SidebarContainer = styled.div<{ $isOpen: boolean }>`
    width: 100%;
    height: 100%;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 2px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
`;

const SidebarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 2px solid ${({ theme }) => theme.lightBorderColor};
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const HeaderTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;

    &:hover {
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const SidebarContent = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    padding: 20px;
`;

const Section = styled.div`
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h4`
    font-size: 12px;
    font-weight: 700;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0 0 12px 0;
`;

const StockInputsRow = styled.div`
    display: flex;
    gap: 12px;

    > * {
        flex: 1;
    }
`;

const SwitchWrapper = styled.div`
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const ActionsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const EmptyState = styled.div`
    padding: 40px 20px;
    text-align: center;
    color: #666;
    font-size: 14px;
`;

const ErrorMessage = styled.div`
    padding: 20px;
    color: #d31500;
    font-size: 14px;
`;

const HelperText = styled.p`
    margin-top: 6px;
    font-size: 12px;
    color: #666;
`;
